import React, { useState, useCallback, useMemo } from "react";
import { AppBar, Toolbar, Button, Menu, MenuItem, IconButton, makeStyles, Fade, Theme, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { HelpOutlined } from "@material-ui/icons";
import { useAdminCurrentUser } from "data/admin/user";
import { useAuth } from "data/auth/hooks";
import UserProfile from "components/UserProfile";
import SystemUsers from "ui/Admin/SystemUsers";
import DropdownMenu, { DropdownMenuItem } from "./headerDropdownMenu";
import AdminIcon from "./RoleSVG/adminSVG";
import TechIcon from "./RoleSVG/techSVG";


export enum Modals {
    ManageSystemUsers = "ManageSystemUsers",
    UserProfile = "UserProfile",
}

export const useStyles = makeStyles((theme: Theme) => ({
    menubar: {
        width: "auto",
        backgroundColor: "white",
        position: "sticky",
        zIndex: 200,
        top: 0,
    },
    toolbar: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        margin: 0,
    },
    leftMenu: {
        display: "flex",
        justifyContent: "flex-start",
        flexGrow: 1,
    },
    centerMenuSpacer: {
        flexGrow: 2,
    },
    rightMenu: {
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        position: "static",
    },
    profileButton: {
        padding: 0,
        minWidth: 0, 
        backgroundColor: "transparent",
        '&:hover': {
            backgroundColor: "transparent",      
        }
    },
    profileButtonContent: {
        display: "inline-flex",
        alignItems: "center",
        flexWrap: "nowrap",
        whiteSpace: "nowrap",
        gap: 4,
        "& $chevron": {
            marginRight: 0,
        },
    },
    profileIconWrapper: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 25,
        height: 25,
        borderRadius: "50%",
        marginRight: 8,
    },
    displayName: {
        fontWeight: 200,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: 220,
        color: theme.palette.text.primary,
        '&:hover': {
            backgroundColor: "transparent",
            color: "#007CAA",
            textDecoration: "underline",          
        }
    },
    chevron: {
        transition: "transform 0.1s",
        verticalAlign: "middle",
        width: 24,
        display: "inline-block",
    },
    chevronOpen: {
        transform: "rotate(180deg)",
    },
}));

const MenuBar = () => {
    const [t] = useTranslation();
    const classes = useStyles();
    const [{ isAuthenticated }, { logout }] = useAuth();
    const [{ user }] = useAdminCurrentUser();

    const [modal, setModal] = useState<Modals | undefined>(undefined);
    const closeModal = useCallback(() => setModal(undefined), []);
    const isModalManageSystemUsers = useMemo(() => modal === Modals.ManageSystemUsers, [modal]);
    const isModalUserProfile = useMemo(() => modal === Modals.UserProfile, [modal]);

    const [menubarItemAnchorEl, setMenubarItemAnchorEl] = useState<null | HTMLElement>(null);
    const isMenuBarItemOpen = Boolean(menubarItemAnchorEl);

    //Temporary Modal for Integrations -> Reporting
    const [isReportingModalOpen, setIsReportingModalOpen] = useState(false);

    //Profile Name string
    const displayName = user ? `${user.firstName} ${user.lastName}`.trim() : "";


    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMenubarItemAnchorEl(event.currentTarget);
    };

    const handleProfileMenuClose = () => {
        setMenubarItemAnchorEl(null);
    };

    //functions for menu items
    const handleProfile = () => {
        setModal(Modals.UserProfile);
        handleProfileMenuClose();

    };
    const handleSystemUsers = () => {
        setModal(Modals.ManageSystemUsers);
        handleProfileMenuClose();
    };
    const handleHelp = () => {
        window.open(
            "https://endress-hauser-commissioningapp-public-assets.s3.us-east-1.amazonaws.com/DCA_manual_external_handin_v1.0.pdf",
            "_blank"
        );
        handleProfileMenuClose();
    };
    const handleLogout = () => {
        logout();
        handleProfileMenuClose();
    };

    const activitiesMenuItems: DropdownMenuItem[] = [
        {
            label: t("headerMenuBar.activities.actions.services"),
            onClick: () => window.open("https://www.us.endress.com/en/instrumentation-services", "_blank", "noopener,noreferrer"),
        },
        {
            label: t("headerMenuBar.activities.actions.commissioning"),
            onClick: () => window.open("https://www.us.endress.com/en/instrumentation-services/project-and-commissioning-services/device-commissioning", "_blank", "noopener,noreferrer"),
        },
        {
            label: t("headerMenuBar.activities.actions.projects"),
            onClick: () => window.open("https://www.us.endress.com/en/endress-hauser-group/capabilities-efficient-safe-operations/process-automation-projects", "_blank", "noopener,noreferrer"),
        },
        {
            label: t("headerMenuBar.activities.actions.miv"),
            onClick: () => window.open("https://www.us.endress.com/en/industry-expertise/water-wastewater/main-instrument-vendor#anchor3", "_blank", "noopener,noreferrer"),
        },
        {
            label: t("headerMenuBar.activities.actions.dynamicIBA"),
            onClick: () => window.open("https://www.us.endress.com/en/instrumentation-services/performance-optimization-services/dynamic-installed-base-analysis", "_blank", "noopener,noreferrer"),
        },
    ];

    const aboutMenuItems: DropdownMenuItem[] = [
        {
            label: t("headerMenuBar.about.actions.digitalCommissioning"),
            onClick: () => window.open("https://www.us.endress.com/en/instrumentation-services/project-and-commissioning-services/digital-commissioning-application", "_blank", "noopener,noreferrer"),
        },
        {
            label: t("headerMenuBar.about.actions.digitizedProjectServices"),
            onClick: () => window.open("https://www.youtube.com/watch?v=URschnCD_YE", "_blank", "noopener,noreferrer"),
        },
        {
            label: t("headerMenuBar.about.actions.connectedWorker"),
            onClick: () => window.open("https://www.us.endress.com/en/endress-hauser-group/Case-studies-application-notes/system-integrator-smt70b", "_blank", "noopener,noreferrer"),
        },
        {
            label: t("headerMenuBar.about.actions.optimization"),
            onClick: () => window.open("https://www.us.endress.com/en/instrumentation-services/performance-optimization-services", "_blank", "noopener,noreferrer"),
        },
        // {
        //     label: t("headerMenuBar.about.actions.netilion"),
        //     onClick: () => window.open("https://netilion.endress.com/", "_blank", "noopener,noreferrer"),
        // },
    ];

    const integrationsMenuItems: DropdownMenuItem[] = [
        {
            label: t("headerMenuBar.integrations.actions.reporting"),
            // onClick: () => window.open("", "_blank", "noopener,noreferrer"),
            onClick: () => {
                setIsReportingModalOpen(true);
            },
            tooltip: "Empowering with Daily Real-Time Reports and Intuitive Dashboards. Stay ahead with actionable insights at your fingertips.",
        },
        {
            label: t("headerMenuBar.integrations.actions.netilionIIOT"),
            onClick: () => window.open("https://www.us.endress.com/en/field-instruments-overview/netilion-iiot", "_blank", "noopener,noreferrer"),
        },
    ];

    const supportMenuItems: DropdownMenuItem[] = [
        {
            label: t("headerMenuBar.support.actions.faqAndHelpArticles"),
            onClick: () => window.open("https://endress-dca-support.zendesk.com/hc/en-us", "_blank", "noopener,noreferrer"),
        },
        {
            label: t("headerMenuBar.support.actions.contact"),
            onClick: () => window.open("https://endress-dca-support.zendesk.com/hc/en-us/requests/new", "_blank", "noopener,noreferrer"),
        },
        {

            label: t("headerMenuBar.support.actions.training"),
            onClick: () => window.open("https://www.us.endress.com/en/instrumentation-services/industry-support-services", "_blank", "noopener,noreferrer"),
        },
    ];


    return (
        <>
            <AppBar className={classes.menubar} elevation={0}>
                <Toolbar className={classes.toolbar} disableGutters>
                    <div className={classes.leftMenu}>
                        {isAuthenticated ? (
                            <>
                                <DropdownMenu
                                    buttonLabel={t("headerMenuBar.integrations.title")}
                                    items={integrationsMenuItems}
                                    chevronClass={classes.chevron}
                                    chevronOpenClass={classes.chevronOpen}
                                />
                                <DropdownMenu
                                    buttonLabel={t("headerMenuBar.support.title")}
                                    items={supportMenuItems}
                                    chevronClass={classes.chevron}
                                    chevronOpenClass={classes.chevronOpen}
                                />
                            </>
                        ) : (
                            <>
                                <DropdownMenu
                                    buttonLabel={t("headerMenuBar.activities.title")}
                                    items={activitiesMenuItems}
                                    chevronClass={classes.chevron}
                                    chevronOpenClass={classes.chevronOpen}
                                />
                                <DropdownMenu
                                    buttonLabel={t("headerMenuBar.about.title")}
                                    items={aboutMenuItems}
                                    chevronClass={classes.chevron}
                                    chevronOpenClass={classes.chevronOpen}
                                />
                                <DropdownMenu
                                    buttonLabel={t("headerMenuBar.integrations.title")}
                                    items={integrationsMenuItems}
                                    chevronClass={classes.chevron}
                                    chevronOpenClass={classes.chevronOpen}
                                />
                                <DropdownMenu
                                    buttonLabel={t("headerMenuBar.support.title")}
                                    items={supportMenuItems}
                                    chevronClass={classes.chevron}
                                    chevronOpenClass={classes.chevronOpen}
                                />
                            </>
                        )}
                    </div>

                    {/* <SearchInput ariaLabel={t("sidebarMenu.search")} placeholder={"Search"} ></SearchInput> */}

                    <div className={classes.centerMenuSpacer} />

                    <div className={classes.rightMenu}>
                        {isAuthenticated && (
                            <Button
                                className={classes.profileButton}
                                color="secondary"
                                onClick={handleProfileMenuOpen}
                            >
                                <span className={classes.profileButtonContent}>
                                    <span className={classes.profileIconWrapper}>
                                        {user?.role === "ProjectManager" ? <AdminIcon /> : <TechIcon />}
                                    </span>
                                    <span className={classes.displayName}>{displayName}</span>
                                    <span className={`${classes.chevron} ${isMenuBarItemOpen ? classes.chevronOpen : ""}`}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" style={{ display: "block" }}>
                                            <polyline points="6 9 12 15 18 9" fill="none" stroke="#AD1457" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span>
                                </span>
                            </Button>
                        )}
                    </div>

                    <Menu
                        anchorEl={menubarItemAnchorEl}
                        open={Boolean(menubarItemAnchorEl)}
                        onClose={handleProfileMenuClose}
                        TransitionComponent={Fade}
                        elevation={0}
                        getContentAnchorEl={null}
                        disableScrollLock
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }} >
                        {user?.isAdmin && (
                            <MenuItem onClick={handleSystemUsers}>{t("headerMenuBar.profileBtn.actions.systemUsers")}</MenuItem>
                        )}
                        <MenuItem onClick={handleProfile}> {t("headerMenuBar.profileBtn.actions.profile")} </MenuItem>
                        <MenuItem onClick={handleHelp}>{t("headerMenuBar.profileBtn.actions.help")}</MenuItem>
                        <MenuItem onClick={handleLogout}>{t("headerMenuBar.profileBtn.actions.logout")}</MenuItem>
                    </Menu>

                    {isModalManageSystemUsers && <SystemUsers open={true} onClose={closeModal} />}
                    {isModalUserProfile && <UserProfile user={user!} onClose={closeModal} />}
                </Toolbar>
            </AppBar >

            {/* Temporary Modal for Integtations->Reporting, 
            to be replaced with URL and remove this dialog
             */}
            <Dialog
                open={isReportingModalOpen}
                onClose={() => setIsReportingModalOpen(false)}
            >
                <DialogTitle >Reporting</DialogTitle>
                <DialogContent>
                    <Typography>
                        Empowering with Daily Real-Time Reports and Intuitive Dashboards. Coming Soon!
                    </Typography>
                    <Typography style={{ fontStyle: "italic", paddingTop: "0.5rem" }}>
                        Stay ahead with actionable insights at your fingertips.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button color="primary" onClick={() => setIsReportingModalOpen(false)}>
                        Back
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default MenuBar;